import { useState } from "react";
import { Play, X } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { Reveal } from "@/components/Reveal";
import { useSite } from "@/context/SiteContext";
import { resolveMedia } from "@/lib/media";

function toEmbed(url) {
  if (!url) return null;
  try {
    const u = new URL(url);
    let id = "";
    if (u.hostname.includes("youtu.be")) id = u.pathname.slice(1);
    else if (u.searchParams.get("v")) id = u.searchParams.get("v");
    else if (u.pathname.includes("/embed/")) id = u.pathname.split("/embed/")[1];
    return id ? `https://www.youtube.com/embed/${id}?autoplay=1&rel=0` : url;
  } catch {
    return url;
  }
}

export default function VideoSection() {
  const { content } = useSite();
  const v = content?.video || {};
  const [open, setOpen] = useState(false);
  const embed = toEmbed(v.youtube_url);

  return (
    <section id="video" className="relative mx-auto max-w-[1400px] px-6 py-24 md:px-10 md:py-32">
      <Reveal className="mb-12 max-w-2xl">
        <span className="font-mono-jb text-xs uppercase tracking-[0.35em] text-[color:var(--aurex-accent-soft)]">
          {v.overline || "Video"}
        </span>
        <h2 className="mt-5 font-display text-4xl font-extrabold tracking-tight text-[color:var(--aurex-text)] sm:text-5xl">
          {v.title}
        </h2>
        <p className="mt-4 text-[color:var(--aurex-muted)]">{v.subtitle}</p>
      </Reveal>

      <Reveal delay={0.1}>
        <button
          data-testid="video-play-btn"
          onClick={() => setOpen(true)}
          className="spotlight group relative block w-full overflow-hidden rounded-[1.6rem] border border-white/10"
        >
          <img
            src={resolveMedia(v.poster)}
            alt="AUREX video"
            className="h-[320px] w-full object-cover transition-transform duration-[1200ms] ease-out group-hover:scale-105 md:h-[560px]"
          />
          <div className="absolute inset-0 bg-black/40" />
          <span className="absolute left-1/2 top-1/2 flex h-20 w-20 -translate-x-1/2 -translate-y-1/2 items-center justify-center rounded-full copper-bg copper-glow transition-transform duration-300 group-hover:scale-110 md:h-24 md:w-24">
            <Play size={30} className="ml-1 text-[color:var(--aurex-on-accent)]" fill="currentColor" />
          </span>
        </button>
      </Reveal>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[70] flex items-center justify-center bg-black/90 p-4"
            onClick={() => setOpen(false)}
            data-testid="video-modal"
          >
            <button
              className="absolute right-6 top-6 flex h-11 w-11 items-center justify-center rounded-full border border-white/15 text-[color:var(--aurex-text)]"
              onClick={() => setOpen(false)}
            >
              <X size={20} />
            </button>
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.92, opacity: 0 }}
              className="aspect-video w-full max-w-4xl overflow-hidden rounded-2xl border border-white/10"
              onClick={(e) => e.stopPropagation()}
            >
              <iframe
                src={embed}
                title="AUREX video"
                className="h-full w-full"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
}
